import { Dispatch, FC, SetStateAction, useCallback } from 'react';
import { SearchType, SearchTypeItem } from '../config';
import { cn } from '@distribute/frontend/utils';
import { Icon } from '../../../shared/ui';
import { useHotkeys } from 'react-hotkeys-hook';

type Props = {
  inputRef: React.RefObject<HTMLInputElement>;
  searchType: SearchType;
  setSearchType: Dispatch<SetStateAction<SearchType>>;
  items: SearchTypeItem[];
};

export const SearchModalSidebar: FC<Props> = ({
  inputRef,
  searchType,
  setSearchType,
  items,
}) => {
  const handleFocus = useCallback(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, [inputRef]);

  useHotkeys(
    'ArrowUp',
    () => {
      setSearchType((prev) => {
        const currentIndex = items.findIndex((item) => item.id === prev);

        return items[currentIndex - 1]?.id ?? items[items.length - 1].id;
      });

      handleFocus();
    },
    { enableOnFormTags: true },
    [items, setSearchType, handleFocus]
  );

  useHotkeys(
    'ArrowDown',
    () => {
      setSearchType((prev) => {
        const currentIndex = items.findIndex((item) => item.id === prev);
        return items[currentIndex + 1]?.id ?? items[0].id;
      });

      handleFocus();
    },
    { enableOnFormTags: true },
    [items, setSearchType, handleFocus]
  );

  return (
    <div className="flex-shrink-0 p-2 ml-2 mb-2 bg-gray-50 rounded-lg">
      <div className="flex flex-col gap-y-2">
        {items.map((item) => (
          <SearchModalSidebarItem
            key={item.id}
            item={item}
            searchType={searchType}
            onChangeSearchType={(s) => {
              setSearchType(s);
              handleFocus();
            }}
          />
        ))}
      </div>
    </div>
  );
};

const SearchModalSidebarItem: FC<{
  item: SearchTypeItem;
  searchType: SearchType;
  onChangeSearchType: (searchType: SearchType) => void;
}> = ({ item, searchType, onChangeSearchType }) => {
  return (
    <button
      type="button"
      key={item.id}
      className={cn(
        'group text-sm font-medium',
        'focus:outline-none',
        'flex flex-nowrap items-center gap-x-3',
        'py-2 px-3 rounded-md',
        {
          'bg-gray-200 text-gray-800': item.id === searchType,
          'text-gray-700 hover:text-gray-800 hover:bg-gray-200':
            item.id !== searchType,
        }
      )}
      onClick={() => onChangeSearchType(item.id)}
    >
      <Icon
        glyph={item.glyph}
        width={20}
        className={cn('flex-shrink-0', {
          'text-gray-500 group-hover:text-gray-600': item.id !== searchType,
          'text-gray-600': item.id === searchType,
        })}
      />
      {item.label}
    </button>
  );
};
